import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFamily } from "@/hooks/useFamily";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, GripVertical, MoreHorizontal, Plus, ChevronUp, ChevronDown, Copy, Trash2 } from "lucide-react";

interface ChecklistItem {
  id: string;
  template_id: string;
  label: string;
  sort_order: number;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  family_id: string;
  child_id: string | null;
}

export default function ChecklistTemplateEditorPage() {
  const { childId } = useParams<{ childId?: string }>();
  const navigate = useNavigate();
  const { familyId } = useFamily();
  const { children } = useAllChildren();
  const queryClient = useQueryClient();

  const isChildMode = !!childId;
  const child = children.find((c) => c.id === childId);

  // ── Templates ──
  const { data: templates = [] } = useQuery({
    queryKey: ["checklist-templates", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_templates")
        .select("*")
        .eq("family_id", familyId!);
      if (error) throw error;
      return data as ChecklistTemplate[];
    },
    enabled: !!familyId,
  });

  const familyTemplate = templates.find((t) => !t.child_id) ?? null;
  const childTemplate = isChildMode
    ? templates.find((t) => t.child_id === childId) ?? null
    : null;

  // In child mode: show child template if it exists, otherwise family template read-only
  // In family mode: show family template
  const activeTemplateId = isChildMode
    ? (childTemplate?.id ?? null)
    : (familyTemplate?.id ?? null);

  // ── Items ──
  const { data: items = [] } = useQuery({
    queryKey: ["checklist-items", activeTemplateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_items")
        .select("*")
        .eq("template_id", activeTemplateId!)
        .order("sort_order");
      if (error) throw error;
      return data as ChecklistItem[];
    },
    enabled: !!activeTemplateId,
  });

  // Family template items (for read-only display when child uses default)
  const { data: familyItems = [] } = useQuery({
    queryKey: ["checklist-items", familyTemplate?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_items")
        .select("*")
        .eq("template_id", familyTemplate!.id)
        .order("sort_order");
      if (error) throw error;
      return data as ChecklistItem[];
    },
    enabled: isChildMode && !childTemplate && !!familyTemplate?.id,
  });

  const displayItems = activeTemplateId ? items : familyItems;
  const isEditable = !!activeTemplateId;

  // ── Mutations ──
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["checklist-items", activeTemplateId] });
  }, [queryClient, activeTemplateId]);

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!newLabel.trim() || !activeTemplateId) return;
      const { error } = await supabase.from("checklist_items").insert({
        template_id: activeTemplateId,
        label: newLabel.trim(),
        sort_order: displayItems.length,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setNewLabel("");
      toast.success("Lagt til");
    },
    onError: () => toast.error("Kunne ikke legge til"),
  });

  const updateLabelMutation = useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) => {
      const { error } = await supabase
        .from("checklist_items")
        .update({ label })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      toast.success("Oppdatert");
    },
    onError: () => toast.error("Kunne ikke oppdatere"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("checklist_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setDeleteTargetId(null);
      toast.success("Fjernet");
    },
    onError: () => toast.error("Kunne ikke fjerne"),
  });

  const moveMutation = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const idx = displayItems.findIndex((i) => i.id === id);
      if (idx < 0) return;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= displayItems.length) return;

      const a = displayItems[idx];
      const b = displayItems[swapIdx];

      const { error: e1 } = await supabase
        .from("checklist_items")
        .update({ sort_order: b.sort_order })
        .eq("id", a.id);
      if (e1) throw e1;

      const { error: e2 } = await supabase
        .from("checklist_items")
        .update({ sort_order: a.sort_order })
        .eq("id", b.id);
      if (e2) throw e2;
    },
    onSuccess: () => invalidate(),
    onError: () => toast.error("Kunne ikke flytte"),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (item: ChecklistItem) => {
      if (!activeTemplateId) return;
      const { error } = await supabase.from("checklist_items").insert({
        template_id: activeTemplateId,
        label: item.label,
        sort_order: displayItems.length,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Duplisert");
    },
    onError: () => toast.error("Kunne ikke duplisere"),
  });

  // ── Create child template from family default ──
  const createChildTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!familyTemplate || !familyId || !childId) throw new Error("Missing data");

      const { data: newTemplate, error: tErr } = await supabase
        .from("checklist_templates")
        .insert({ family_id: familyId, child_id: childId, name: "Standard bytte" })
        .select()
        .single();
      if (tErr) throw tErr;

      const { data: sourceItems, error: siErr } = await supabase
        .from("checklist_items")
        .select("label, sort_order")
        .eq("template_id", familyTemplate.id)
        .order("sort_order");
      if (siErr) throw siErr;

      if (sourceItems && sourceItems.length > 0) {
        const { error: iErr } = await supabase.from("checklist_items").insert(
          sourceItems.map((item) => ({
            template_id: newTemplate.id,
            label: item.label,
            sort_order: item.sort_order,
          }))
        );
        if (iErr) throw iErr;
      }

      return newTemplate.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-templates"] });
      toast.success("Egen mal opprettet");
    },
    onError: () => toast.error("Kunne ikke opprette mal"),
  });

  // ── Reset child template to family default ──
  const resetToDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!childTemplate) throw new Error("No child template");
      // Delete child template items first
      const { error: delErr } = await supabase
        .from("checklist_items")
        .delete()
        .eq("template_id", childTemplate.id);
      if (delErr) throw delErr;

      // Delete the child template
      const { error: tErr } = await supabase
        .from("checklist_templates")
        .delete()
        .eq("id", childTemplate.id);
      if (tErr) throw tErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-templates"] });
      queryClient.invalidateQueries({ queryKey: ["checklist-items"] });
      toast.success("Tilbakestilt til standardmalen");
    },
    onError: () => toast.error("Kunne ikke tilbakestille"),
  });

  // ── Copy from family default into child template ──
  const copyFromDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!familyTemplate || !activeTemplateId) throw new Error("Missing");
      const { data: sourceItems, error } = await supabase
        .from("checklist_items")
        .select("label, sort_order")
        .eq("template_id", familyTemplate.id)
        .order("sort_order");
      if (error) throw error;

      if (sourceItems && sourceItems.length > 0) {
        const maxSort = displayItems.length > 0 ? Math.max(...displayItems.map((i) => i.sort_order)) + 1 : 0;
        const { error: iErr } = await supabase.from("checklist_items").insert(
          sourceItems.map((item, idx) => ({
            template_id: activeTemplateId,
            label: item.label,
            sort_order: maxSort + idx,
          }))
        );
        if (iErr) throw iErr;
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success("Kopiert fra standardmal");
    },
    onError: () => toast.error("Kunne ikke kopiere"),
  });

  // ── Header ──
  const title = isChildMode
    ? `${child?.avatar_emoji ?? "👶"} Mal for ${child?.name ?? "barn"}`
    : "Standardmal";

  return (
    <div className="min-h-full bg-background px-4 pt-safe pb-6">
      <div className="mx-auto max-w-sm space-y-6">
        {/* Back + title */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate("/settings")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl">{title}</h1>
          </div>
        </div>

        <Badge variant="secondary" className="text-xs">
          Gjelder kun nye sjekklister
        </Badge>

        {/* ── Child mode: uses default ── */}
        {isChildMode && !childTemplate && (
          <Card>
            <CardContent className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                Bruker standardmalen. Elementene nedenfor er ikke redigerbare her.
              </p>

              {/* Read-only family items */}
              {familyItems.length > 0 && (
                <div className="space-y-1.5">
                  {familyItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-lg border p-3 opacity-60"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground/30" />
                      <span className="flex-1 text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full"
                variant="outline"
                onClick={() => createChildTemplateMutation.mutate()}
                disabled={createChildTemplateMutation.isPending}
              >
                {createChildTemplateMutation.isPending ? "Oppretter…" : "Lag egen mal for dette barnet"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Editable items ── */}
        {isEditable && (
          <Card>
            <CardContent className="p-4 space-y-3">
              {displayItems.length === 0 && (
                <p className="text-sm text-muted-foreground py-3 text-center">
                  Ingen elementer ennå.
                </p>
              )}

              {displayItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg border p-2.5 group"
                >
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20 transition-colors p-0.5"
                      disabled={idx === 0 || moveMutation.isPending}
                      onClick={() => moveMutation.mutate({ id: item.id, direction: "up" })}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20 transition-colors p-0.5"
                      disabled={idx === displayItems.length - 1 || moveMutation.isPending}
                      onClick={() => moveMutation.mutate({ id: item.id, direction: "down" })}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Label (inline edit) */}
                  {editingId === item.id ? (
                    <Input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 h-8 text-sm"
                      
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && editText.trim()) {
                          updateLabelMutation.mutate({ id: item.id, label: editText.trim() });
                        }
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => {
                        if (editText.trim() && editText.trim() !== item.label) {
                          updateLabelMutation.mutate({ id: item.id, label: editText.trim() });
                        } else {
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <span
                      className="flex-1 text-sm cursor-pointer hover:text-primary transition-colors"
                      onClick={() => {
                        setEditingId(item.id);
                        setEditText(item.label);
                      }}
                    >
                      {item.label}
                    </span>
                  )}

                  {/* Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-muted-foreground/40 hover:text-foreground transition-colors p-1 opacity-0 group-hover:opacity-100 focus:opacity-100">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setEditingId(item.id);
                          setEditText(item.label);
                        }}
                      >
                        Rediger
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateMutation.mutate(item)}>
                        <Copy className="h-3.5 w-3.5 mr-2" />
                        Dupliser
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setDeleteTargetId(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Slett
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}

              {/* Add new */}
              <div className="flex gap-2 pt-2 border-t border-border">
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Nytt element…"
                  className="flex-1 h-9 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newLabel.trim()) addMutation.mutate();
                  }}
                />
                <Button
                  size="sm"
                  className="h-9"
                  onClick={() => addMutation.mutate()}
                  disabled={!newLabel.trim() || addMutation.isPending}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Child-specific actions ── */}
        {isChildMode && childTemplate && (
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              onClick={() => copyFromDefaultMutation.mutate()}
              disabled={copyFromDefaultMutation.isPending}
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Kopier fra standardmalen
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full text-xs text-destructive">
                  Tilbakestill til standardmalen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Tilbakestill til standardmal?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Barnets egne mal-elementer slettes og standardmalen brukes igjen. Dette påvirker kun nye sjekklister.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => resetToDefaultMutation.mutate()}
                  >
                    Tilbakestill
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Delete confirmation dialog */}
        <AlertDialog
          open={!!deleteTargetId}
          onOpenChange={(open) => !open && setDeleteTargetId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Slett element?</AlertDialogTitle>
              <AlertDialogDescription>
                Elementet fjernes fra malen. Eksisterende sjekklister påvirkes ikke.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deleteTargetId) deleteMutation.mutate(deleteTargetId);
                }}
              >
                Slett
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
